import React, { ReactElement, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Typography,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import dayjs from 'dayjs'
import { AddIcon, DeleteIcon } from 'src/v3/core/icons'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  deletePendingVirginia,
  deletePendingVirginiaCourse,
  deleteVirginia,
  deleteVirginiaCourse,
  finalizePendingVirginia,
  finalizePendingVirginiaCourse,
  clearVirginiaOperationStatus,
  selectVirginiaOperationStatus,
  selectVirginiaOperationError,
  selectVirginiaOperationSuccess,
} from 'src/v3/content-pipeline/accreditation.slice'
import ConfirmAction from 'src/v3/components/confirm-action'
import {
  VirginiaPendingAccreditation,
  VirginiaFinalizedAccreditation,
} from './types'
import { CreateForm } from './create-form'
import { EditForm } from './edit-form'
import { FieldComparison, StatusActions, AutoTrackedDates } from '../shared'
import {
  getStatusColor,
  getAccreditationFlags,
  RejectedAlert,
  ApprovedAlert,
  OperationSnackbars,
  calculateFieldChanges,
} from '../../shared-utils'

type VirginiaSectionProps = {
  eventId?: string
  courseId?: string
  finalized?: VirginiaFinalizedAccreditation
  pending?: VirginiaPendingAccreditation
}

export let VirginiaSection = ({
  eventId,
  courseId,
  finalized,
  pending,
}: VirginiaSectionProps): ReactElement => {
  let dispatch = useAppDispatch()
  let [formOpen, setFormOpen] = useState(false)
  let [editFormOpen, setEditFormOpen] = useState(false)
  let [expanded, setExpanded] = useState(false)
  let [confirm, setConfirm] = useState('')

  let operationStatus = useAppSelector(selectVirginiaOperationStatus)
  let operationError = useAppSelector(selectVirginiaOperationError)
  let operationSuccess = useAppSelector(selectVirginiaOperationSuccess)

  let submitting = operationStatus === 'pending'
  let { hasNoAccreditation, hasOnlyFinalized, hasOnlyPending, hasBoth } =
    getAccreditationFlags(finalized, pending)
  let statusColor = getStatusColor(pending?.status)

  let handleFinalize = () => {
    if (!pending?.id) return
    if (courseId) {
      dispatch(finalizePendingVirginiaCourse({ id: pending.id, courseId }))
    } else if (eventId) {
      dispatch(finalizePendingVirginia({ id: pending.id, eventId }))
    }
  }

  let changes = calculateFieldChanges(finalized, pending, [
    { key: 'topic', label: 'Topic' },
    { key: 'subTopic', label: 'Sub-Topic' },
    { key: 'areaNumberALS', label: 'Area Number (ALS)' },
    { key: 'areaNumberBLS', label: 'Area Number (BLS)' },
    { key: 'expirationDate', label: 'Expiration Date' },
  ])

  return (
    <Box>
      <Card variant="outlined" sx={{ overflow: 'visible' }}>
        <Box className="p-4 grid grid-cols-[1fr_auto] gap-4 items-center">
          <Box className="flex items-center gap-4">
            <Typography variant="h6">Virginia Accreditation</Typography>
            {finalized && (
              <>
                <Chip color="success" label="Finalized" size="small" />
                <Typography color="text.secondary" variant="body2">
                  {finalized.topic} • {finalized.subTopic}
                </Typography>
              </>
            )}
            {hasOnlyPending && pending && (
              <>
                <Chip
                  label={pending.status}
                  size="small"
                  sx={{
                    color: statusColor,
                  }}
                />
                <Typography color="text.secondary" variant="body2">
                  {pending.topic || 'Pending'} • {pending.subTopic || 'Draft'}
                </Typography>
              </>
            )}
            {hasNoAccreditation && (
              <Chip color="default" label="Not Created" size="small" />
            )}
          </Box>

          <Box className="flex items-center gap-2">
            {hasBoth && pending && (
              <Chip
                color="warning"
                label={`${changes.length} ${changes.length === 1 ? 'Change' : 'Changes'}`}
                size="small"
              />
            )}
            {hasBoth && pending && (
              <Chip
                label={pending.status}
                size="small"
                sx={{
                  color: statusColor,
                }}
              />
            )}
            {(hasNoAccreditation || hasOnlyFinalized) && (
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                size="small"
                onClick={() => setFormOpen(true)}
              >
                {hasNoAccreditation ? 'Create' : 'New Update'}
              </Button>
            )}
            {(finalized || pending) && (
              <IconButton
                size="small"
                onClick={() => setExpanded(!expanded)}
                aria-label={expanded ? 'Show less' : 'Show more'}
              >
                {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            )}
          </Box>
        </Box>

        {pending?.status === 'Rejected' && (
          <RejectedAlert
            rejectedDate={pending.rejectedDate}
            notes={pending.notes}
          />
        )}

        {pending?.status === 'Approved' && (
          <ApprovedAlert approvedDate={pending.approvedDate} />
        )}

        <Collapse in={expanded}>
          <Divider />
          <Box sx={{ p: 2 }}>
            {hasNoAccreditation && (
              <Typography color="text.secondary" variant="body2">
                No Virginia accreditation has been created for this{' '}
                {courseId ? 'course' : 'event'}.
              </Typography>
            )}

            {hasBoth && finalized && pending && (
              <Box>
                <Box className="grid grid-cols-2 gap-4 mb-4">
                  <Typography
                    color="text.secondary"
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    Current (Finalized)
                  </Typography>
                  <Typography
                    color="text.secondary"
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    Pending Update
                  </Typography>
                </Box>

                <FieldComparison
                  label="Topic"
                  finalizedValue={finalized.topic}
                  pendingValue={pending.topic}
                />
                <FieldComparison
                  label="Sub-Topic"
                  finalizedValue={finalized.subTopic}
                  pendingValue={pending.subTopic}
                />
                <FieldComparison
                  label="Area Number (ALS)"
                  finalizedValue={finalized.areaNumberALS}
                  pendingValue={pending.areaNumberALS}
                />
                <FieldComparison
                  label="Area Number (BLS)"
                  finalizedValue={finalized.areaNumberBLS}
                  pendingValue={pending.areaNumberBLS}
                />
                <FieldComparison
                  label="Expiration Date"
                  finalizedValue={finalized.expirationDate ?? ''}
                  pendingValue={pending.expirationDate}
                  isDate
                />

                {pending.notes && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paperDark' }}>
                    <Typography
                      color="text.secondary"
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      Notes
                    </Typography>
                    <Typography variant="body2">{pending.notes}</Typography>
                  </Box>
                )}

                <StatusActions
                  status={pending.status}
                  submitting={submitting}
                  onEdit={() => setEditFormOpen(true)}
                  onDelete={() => setConfirm('delete-pending-virginia')}
                  onFinalize={handleFinalize}
                />
              </Box>
            )}

            {hasOnlyFinalized && finalized && (
              <Box>
                <Box className="grid gap-3">
                  <Box className="grid grid-cols-2 gap-4">
                    <Box>
                      <Typography color="text.secondary" variant="caption">
                        Topic
                      </Typography>
                      <Typography variant="body2">{finalized.topic}</Typography>
                    </Box>
                    <Box>
                      <Typography color="text.secondary" variant="caption">
                        Sub-Topic
                      </Typography>
                      <Typography variant="body2">
                        {finalized.subTopic}
                      </Typography>
                    </Box>
                  </Box>
                  <Box className="grid grid-cols-2 gap-4">
                    <Box>
                      <Typography color="text.secondary" variant="caption">
                        Area Number (ALS)
                      </Typography>
                      <Typography variant="body2">
                        {finalized.areaNumberALS}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography color="text.secondary" variant="caption">
                        Area Number (BLS)
                      </Typography>
                      <Typography variant="body2">
                        {finalized.areaNumberBLS}
                      </Typography>
                    </Box>
                  </Box>
                  {finalized.expirationDate && (
                    <Box>
                      <Typography color="text.secondary" variant="caption">
                        Expiration Date
                      </Typography>
                      <Typography variant="body2">
                        {dayjs(finalized.expirationDate).format('MMM D, YYYY')}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box className="flex justify-end mt-3">
                  <Button
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => setConfirm('delete-finalized-virginia')}
                    disabled={submitting}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>
            )}

            {hasOnlyPending && pending && (
              <Box>
                <Box className="grid gap-3">
                  {pending.topic && pending.subTopic && (
                    <Box className="grid grid-cols-2 gap-4">
                      <Box>
                        <Typography color="text.secondary" variant="caption">
                          Topic
                        </Typography>
                        <Typography variant="body2">{pending.topic}</Typography>
                      </Box>
                      <Box>
                        <Typography color="text.secondary" variant="caption">
                          Sub-Topic
                        </Typography>
                        <Typography variant="body2">
                          {pending.subTopic}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {(pending.areaNumberALS || pending.areaNumberBLS) && (
                    <Box className="grid grid-cols-2 gap-4">
                      {pending.areaNumberALS && (
                        <Box>
                          <Typography color="text.secondary" variant="caption">
                            Area Number (ALS)
                          </Typography>
                          <Typography variant="body2">
                            {pending.areaNumberALS}
                          </Typography>
                        </Box>
                      )}
                      {pending.areaNumberBLS && (
                        <Box>
                          <Typography color="text.secondary" variant="caption">
                            Area Number (BLS)
                          </Typography>
                          <Typography variant="body2">
                            {pending.areaNumberBLS}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                  {pending.expirationDate && (
                    <Box>
                      <Typography color="text.secondary" variant="caption">
                        Expiration Date
                      </Typography>
                      <Typography variant="body2">
                        {dayjs(pending.expirationDate).format('MMM D, YYYY')}
                      </Typography>
                    </Box>
                  )}
                  <AutoTrackedDates
                    submittedDate={pending.submittedDate}
                    approvedDate={pending.approvedDate}
                    rejectedDate={pending.rejectedDate}
                  />
                  {pending.notes && (
                    <Box sx={{ p: 2, bgcolor: 'background.paperDark' }}>
                      <Typography
                        color="text.secondary"
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                        }}
                      >
                        Notes
                      </Typography>
                      <Typography variant="body2">{pending.notes}</Typography>
                    </Box>
                  )}
                </Box>

                <StatusActions
                  status={pending.status}
                  submitting={submitting}
                  onEdit={() => setEditFormOpen(true)}
                  onDelete={() => setConfirm('delete-pending-virginia')}
                  onFinalize={handleFinalize}
                />
              </Box>
            )}
          </Box>
        </Collapse>
      </Card>
      <CreateForm
        eventId={eventId}
        courseId={courseId}
        open={formOpen}
        setOpen={setFormOpen}
        onSuccess={() => {}}
      />
      {pending && (
        <EditForm
          eventId={eventId}
          courseId={courseId}
          pending={pending}
          finalized={finalized}
          open={editFormOpen}
          setOpen={setEditFormOpen}
          onSuccess={() => {}}
        />
      )}
      {pending && (
        <ConfirmAction
          open={confirm}
          setOpen={setConfirm}
          openText="delete-pending-virginia"
          title="Delete Pending Virginia?"
          text="Are you sure you want to delete this pending Virginia accreditation? This action cannot be undone."
          confirmText="Delete"
          executeAction={async () => {
            if (courseId) {
              dispatch(
                deletePendingVirginiaCourse({ id: pending.id, courseId }),
              )
            } else if (eventId) {
              dispatch(deletePendingVirginia({ id: pending.id, eventId }))
            }
          }}
        />
      )}
      {finalized && (
        <ConfirmAction
          open={confirm}
          setOpen={setConfirm}
          openText="delete-finalized-virginia"
          title="Delete Virginia Accreditation?"
          text="Are you sure you want to delete this Virginia accreditation? This action cannot be undone."
          confirmText="Delete"
          executeAction={async () => {
            if (courseId) {
              dispatch(deleteVirginiaCourse({ id: finalized.id, courseId }))
            } else if (eventId) {
              dispatch(deleteVirginia({ id: finalized.id, eventId }))
            }
          }}
        />
      )}
      <OperationSnackbars
        successMessage={operationSuccess}
        errorMessage={operationError}
        onClear={() => dispatch(clearVirginiaOperationStatus())}
      />
    </Box>
  )
}
